/**
 * CONTROL DE ESTERILIZACIÓN Y CONTROL DE ESPORAS (administración, 5/10/2026)
 * ------------------------------------------------------------------------
 * «Ambos deben funcionar de la misma manera: un calendario que permita
 * seleccionar un día y mostrar todos los registros de esa fecha; para crear un
 * nuevo registro, introducir la fecha y adjuntar una foto (subiéndola desde el
 * dispositivo o haciéndola directamente con la cámara); los registros de cada
 * día deben quedar almacenados y ser consultables seleccionando la fecha en el
 * calendario.»
 *
 * Una sola pantalla para los dos subapartados del menú Quirófano, con el tipo
 * como parámetro (`tipo`: «esterilizacion» | «esporas»), igual que en el
 * servidor es un solo modelo (routers/controles_quirofano_router.py).
 *
 *  - El calendario es el de la agenda (components/ui/calendar) y marca con un
 *    punto los días del mes que tienen registros.
 *  - La lista del día enseña la miniatura (se abre en grande y se puede
 *    acercar, como las fotos del paciente), la nota y quién y a qué hora.
 *  - «Nuevo registro»: fecha (por defecto, hoy), la foto —«Hacer foto» abre
 *    la cámara trasera en el móvil y la tablet (`capture="environment"`, como
 *    la página de los pasos del paciente); «Subir desde el dispositivo», la
 *    galería o los archivos— y una nota opcional corta.
 *  - Borrar, solo a quien el servidor le deja (`puede_borrar`: quien lo
 *    apuntó, el mismo día, o dirección). Es un borrado blando con rastro.
 *
 * Se usa en el quirófano, en el móvil o la tablet: botones grandes, días del
 * calendario de 40 px y la lista en una columna cuando no cabe más.
 * Sin emojis (no se ven en los equipos de la clínica): iconos de lucide.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { es } from 'date-fns/locale';
import { format } from 'date-fns';
import {
  Camera, Upload, Plus, Trash2, Loader2, Clock, ImageOff, RefreshCw, CalendarDays,
  ShieldPlus, FlaskConical,
} from 'lucide-react';
import { api } from '../lib/api';
import { cn } from '../lib/utils';
import { PageHeader } from '../components/Common';
import { Calendar } from '../components/ui/calendar';
import { Button, buttonVariants } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import FotoZoom from '../components/fotos/FotoZoom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const TIPOS_CONTROL = {
  esterilizacion: {
    titulo: 'Control de esterilización',
    subtitulo: 'Elige un día en el calendario para ver sus registros de esterilización.',
    icono: ShieldPlus,
  },
  esporas: {
    titulo: 'Control de esporas',
    subtitulo: 'Elige un día en el calendario para ver sus registros de control de esporas.',
    icono: FlaskConical,
  },
};

// Los mismos límites que las fotos del paciente (el servidor los vuelve a
// mirar; esto solo avisa antes de subir 30 MB para nada).
const MAX_FOTO_BYTES = 25 * 1024 * 1024;
const EXTENSIONES_FOTO = /\.(jpe?g|png|webp|heic|heif|gif)$/i;
const NOTA_MAXIMA = 300;

const dosCifras = (n) => String(n).padStart(2, '0');
const aTexto = (d) => `${d.getFullYear()}-${dosCifras(d.getMonth() + 1)}-${dosCifras(d.getDate())}`;
const aFecha = (s) => {
  const [y, m, d] = String(s || '').split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const hoyTexto = () => aTexto(new Date());
const mesDe = (s) => String(s || '').slice(0, 7);
const fechaCorta = (s) => (s && s.length === 10 ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : s || '');
const fechaLarga = (s) => {
  const t = format(aFecha(s), "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
  return t.charAt(0).toUpperCase() + t.slice(1);
};
const hora = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};
const esImagen = (f) => !!f && ((f.type || '').startsWith('image/') || EXTENSIONES_FOTO.test(f.name || ''));
const fotoUrl = (r, tam) => `${BACKEND_URL}/api/controles-quirofano/${r.id}/foto${tam ? `?tam=${tam}` : ''}`;
const detalleError = (e, porDefecto) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' && d ? d : porDefecto;
};

/** La miniatura de un registro: si la foto no carga, lo dice en vez de
 *  dejar un hueco en blanco. */
function Miniatura({ registro, alt, onAbrir }) {
  const [fallo, setFallo] = useState(false);
  return (
    <button
      type="button"
      onClick={onAbrir}
      className="block w-full bg-[#EEF2F0] cursor-zoom-in"
      aria-label="Ver la foto en grande"
      data-testid="control-abrir-foto"
    >
      {fallo ? (
        <span className="h-48 flex flex-col items-center justify-center gap-1 text-xs text-muted-foreground">
          <ImageOff className="w-6 h-6" /> No se pudo cargar la foto
        </span>
      ) : (
        <img
          src={fotoUrl(registro, 'mini')}
          alt={alt}
          loading="lazy"
          onError={() => setFallo(true)}
          className="w-full h-48 object-contain"
          data-testid="control-miniatura"
        />
      )}
    </button>
  );
}

/** «Nuevo registro»: fecha, foto (cámara o dispositivo) y nota. */
function NuevoRegistroDialog({ open, onClose, tipo, onGuardado }) {
  const conf = TIPOS_CONTROL[tipo];
  const [fecha, setFecha] = useState(hoyTexto);
  const [foto, setFoto] = useState(null);           // File elegido
  const [vista, setVista] = useState('');           // URL de la vista previa
  const [vistaFallo, setVistaFallo] = useState(false);
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const camaraRef = useRef(null);
  const archivoRef = useRef(null);

  // Al abrir, de cero: hoy, sin foto y sin nota.
  useEffect(() => {
    if (!open) return;
    setFecha(hoyTexto());
    setFoto(null);
    setNota('');
    setError('');
    setVistaFallo(false);
  }, [open]);

  // La vista previa vive lo que vive la foto elegida.
  useEffect(() => {
    if (!foto) { setVista(''); return undefined; }
    const url = URL.createObjectURL(foto);
    setVista(url);
    setVistaFallo(false);
    return () => { try { URL.revokeObjectURL(url); } catch { /* nada */ } };
  }, [foto]);

  const elegir = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!esImagen(f)) {
      setError('Eso no es una foto. Usa una imagen (JPG, PNG, HEIC…).');
      return;
    }
    if (f.size > MAX_FOTO_BYTES) {
      setError('La foto es demasiado grande (máx. 25 MB).');
      return;
    }
    setError('');
    setFoto(f);
  };

  const guardar = async () => {
    if (!fecha) { setError('Pon la fecha del control.'); return; }
    if (fecha > hoyTexto()) { setError('La fecha no puede ser posterior a hoy.'); return; }
    if (!foto) { setError('Adjunta una foto del control.'); return; }
    setError('');
    setGuardando(true);
    const fd = new FormData();
    fd.append('tipo', tipo);
    fd.append('fecha', fecha);
    fd.append('nota', nota.trim());
    fd.append('file', foto);
    try {
      const r = await api.post('/api/controles-quirofano', fd);
      toast.success('Registro guardado');
      onGuardado(r.data || { fecha });
    } catch (e) {
      setError(detalleError(e, 'No se pudo guardar el registro. Vuelve a intentarlo.'));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !guardando) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="control-nuevo-dialogo">
        <DialogHeader>
          <DialogTitle className="text-[#13231E]">Nuevo registro</DialogTitle>
          <DialogDescription>{conf.titulo}: la fecha, la foto y, si hace falta, una nota.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor={`control-fecha-${tipo}`}>Fecha</Label>
            <Input
              id={`control-fecha-${tipo}`}
              type="date"
              value={fecha}
              max={hoyTexto()}
              onChange={(e) => setFecha(e.target.value)}
              className="h-11 w-full sm:w-52"
              data-testid="control-nuevo-fecha"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Foto</Label>
            {/* Cámara y archivo por separado: en el móvil y la tablet son dos
                cosas distintas. `capture` abre la cámara trasera directamente;
                en el ordenador se ignora y abre el selector de archivos. */}
            <input
              ref={camaraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={elegir}
              data-testid="control-nuevo-camara-input"
            />
            <input
              ref={archivoRef}
              type="file"
              accept="image/*,.heic,.heif"
              className="hidden"
              onChange={elegir}
              data-testid="control-nuevo-archivo-input"
            />
            {foto && (
              <div className="rounded-lg border border-border bg-[#EEF2F0] overflow-hidden" data-testid="control-nuevo-vista">
                {vista && !vistaFallo ? (
                  <img src={vista} alt="Foto elegida" className="w-full max-h-64 object-contain" onError={() => setVistaFallo(true)} />
                ) : (
                  // Un HEIC no se ve en todos los navegadores, pero se sube igual.
                  <div className="px-3 py-6 text-center text-sm text-[#13231E]">Foto lista: {foto.name}</div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-11 border-[#2E6E5E] text-[#2E6E5E]"
                onClick={() => camaraRef.current?.click()}
                disabled={guardando}
                data-testid="control-nuevo-camara"
              >
                <Camera className="w-4 h-4 mr-2" /> {foto ? 'Repetir foto' : 'Hacer foto'}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-11"
                onClick={() => archivoRef.current?.click()}
                disabled={guardando}
                data-testid="control-nuevo-archivo"
              >
                <Upload className="w-4 h-4 mr-2" /> {foto ? 'Elegir otra' : 'Subir desde el dispositivo'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">JPG, PNG o HEIC; máx. 25 MB.</p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`control-nota-${tipo}`}>Nota (opcional)</Label>
            <Textarea
              id={`control-nota-${tipo}`}
              value={nota}
              maxLength={NOTA_MAXIMA}
              rows={2}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ej.: ciclo 2, autoclave pequeño"
              data-testid="control-nuevo-nota"
            />
          </div>

          {error && (
            <p className="text-sm text-red-700" role="alert" data-testid="control-nuevo-error">{error}</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" className="h-11" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button
            type="button"
            className="h-11 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            onClick={guardar}
            disabled={guardando}
            data-testid="control-nuevo-guardar"
          >
            {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            {guardando ? 'Guardando…' : 'Guardar registro'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ControlQuirofanoPage({ tipo = 'esterilizacion' }) {
  const conf = TIPOS_CONTROL[tipo] || TIPOS_CONTROL.esterilizacion;
  const Icono = conf.icono;
  const [fecha, setFecha] = useState(hoyTexto);
  const [mes, setMes] = useState(() => aFecha(hoyTexto()));
  const [registros, setRegistros] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [errorCarga, setErrorCarga] = useState('');
  const [dias, setDias] = useState([]);          // [{fecha, registros}]
  const [recarga, setRecarga] = useState(0);     // sube para volver a pedir
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [visor, setVisor] = useState(null);      // registro abierto en grande
  const [borrando, setBorrando] = useState(null);

  const mesTexto = aTexto(mes).slice(0, 7);

  // Los registros del día elegido.
  useEffect(() => {
    let vivo = true;
    setCargando(true);
    setErrorCarga('');
    api.get(`/api/controles-quirofano?tipo=${tipo}&fecha=${fecha}`)
      .then((r) => { if (vivo) setRegistros(r.data?.registros || []); })
      .catch((e) => {
        if (!vivo) return;
        setRegistros([]);
        setErrorCarga(detalleError(e, 'No se pudieron cargar los registros de este día.'));
      })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [tipo, fecha, recarga]);

  // Los días del mes a la vista que tienen registros (los puntos).
  useEffect(() => {
    let vivo = true;
    api.get(`/api/controles-quirofano/dias?tipo=${tipo}&mes=${mesTexto}`)
      .then((r) => { if (vivo) setDias(r.data?.dias || []); })
      .catch(() => { if (vivo) setDias([]); });
    return () => { vivo = false; };
  }, [tipo, mesTexto, recarga]);

  const diasMarcados = useMemo(() => dias.map((d) => aFecha(d.fecha)), [dias]);

  const irA = (texto) => {
    setFecha(texto);
    if (mesDe(texto) !== mesTexto) setMes(aFecha(`${mesDe(texto)}-01`));
  };

  const alGuardar = (registro) => {
    setNuevoOpen(false);
    // Se va al día del registro, que es donde se tiene que ver.
    if (registro?.fecha) irA(registro.fecha);
    setRecarga((n) => n + 1);
  };

  const borrar = async (registro) => {
    if (!window.confirm('¿Borrar este registro? Dejará de verse en el calendario (queda constancia en Control rastreo).')) return;
    setBorrando(registro.id);
    try {
      await api.delete(`/api/controles-quirofano/${registro.id}`);
      toast.success('Registro borrado');
      if (visor?.id === registro.id) setVisor(null);
      setRecarga((n) => n + 1);
    } catch (e) {
      toast.error(detalleError(e, 'No se pudo borrar el registro'));
    } finally {
      setBorrando(null);
    }
  };

  const esHoy = fecha === hoyTexto();

  return (
    <div data-testid={`control-pagina-${tipo}`}>
      <PageHeader
        title={conf.titulo}
        subtitle={conf.subtitulo}
        actions={(
          <Button
            className="h-11 bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            onClick={() => setNuevoOpen(true)}
            data-testid="control-nuevo"
          >
            <Plus className="w-4 h-4 mr-2" /> Nuevo registro
          </Button>
        )}
      />

      <div className="grid gap-4 lg:grid-cols-[auto_minmax(0,1fr)] items-start">
        <section className="cjo-card p-2 flex flex-col items-center" data-testid="control-calendario">
          <Calendar
            mode="single"
            locale={es}
            selected={aFecha(fecha)}
            month={mes}
            onMonthChange={setMes}
            onSelect={(d) => { if (d) irA(aTexto(d)); }}
            modifiers={{ conRegistros: diasMarcados }}
            // El punto de los días con registros (y blanco sobre el día elegido).
            modifiersClassNames={{
              conRegistros: "control-dia-con-registros relative font-semibold after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1.5 after:w-1.5 after:rounded-full after:bg-[#2E6E5E] aria-selected:after:bg-white",
            }}
            // Días de 40 px: se tocan con el dedo (quirófano, móvil y tablet).
            // Hoy, con borde y no relleno: relleno es el día elegido (con el
            // relleno de la agenda los dos se veían iguales y el punto de hoy
            // no se veía).
            classNames={{
              head_cell: 'text-muted-foreground rounded-md w-10 font-normal text-[0.8rem]',
              day: cn(buttonVariants({ variant: 'ghost' }), 'h-10 w-10 p-0 font-normal aria-selected:opacity-100'),
              day_today: 'border border-[#2E6E5E] text-[#2E6E5E] font-semibold',
            }}
          />
          <div className="w-full flex items-center justify-between gap-2 px-3 pb-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2E6E5E]" /> Días con registros
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => irA(hoyTexto())}
              disabled={esHoy}
              data-testid="control-hoy"
            >
              Hoy
            </Button>
          </div>
        </section>

        <section className="min-w-0" data-testid="control-dia">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-3">
            <Icono className="w-5 h-5 text-[#2E6E5E] shrink-0" />
            <h2 className="font-display text-lg font-semibold text-[#13231E]" data-testid="control-dia-titulo">
              {fechaLarga(fecha)}
            </h2>
            {!cargando && !errorCarga && (
              <span className="rounded-full bg-[rgba(46,110,94,0.10)] px-2 py-0.5 text-xs font-medium text-[#13231E]" data-testid="control-dia-cuenta">
                {registros.length === 1 ? '1 registro' : `${registros.length} registros`}
              </span>
            )}
          </div>

          {cargando ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando registros…
            </div>
          ) : errorCarga ? (
            <div className="cjo-card p-5 text-sm" data-testid="control-error">
              <p className="text-red-700">{errorCarga}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setRecarga((n) => n + 1)}>
                <RefreshCw className="w-4 h-4 mr-1.5" /> Reintentar
              </Button>
            </div>
          ) : registros.length === 0 ? (
            <div className="cjo-card p-8 text-center" data-testid="control-vacio">
              <CalendarDays className="w-8 h-8 mx-auto mb-2 text-[#2E6E5E] opacity-60" />
              <p className="text-sm text-muted-foreground">No hay registros de este día.</p>
              <Button
                variant="outline"
                className="mt-4 h-11 border-[#2E6E5E] text-[#2E6E5E]"
                onClick={() => setNuevoOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" /> Nuevo registro
              </Button>
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="control-lista">
              {registros.map((r) => (
                <li key={r.id} className="cjo-card overflow-hidden flex flex-col" data-testid="control-registro" data-id={r.id}>
                  <Miniatura
                    registro={r}
                    alt={`${conf.titulo} del ${fechaCorta(r.fecha)}`}
                    onAbrir={() => setVisor(r)}
                  />
                  <div className="p-3 text-sm flex flex-col gap-1.5 flex-1">
                    <div className="flex items-center gap-1.5 text-[#13231E]">
                      <Clock className="w-3.5 h-3.5 text-[#2E6E5E] shrink-0" />
                      <span className="font-medium">{hora(r.creado_at)}</span>
                      <span className="text-muted-foreground truncate" data-testid="control-autor">
                        · {r.creado_por_nombre || 'Sin nombre'}
                      </span>
                    </div>
                    {r.nota ? (
                      <p className="text-[#13231E] break-words" data-testid="control-nota">{r.nota}</p>
                    ) : (
                      <p className="text-muted-foreground text-xs">Sin nota</p>
                    )}
                    {r.puede_borrar && (
                      <button
                        type="button"
                        onClick={() => borrar(r)}
                        disabled={borrando === r.id}
                        className="self-start mt-auto inline-flex items-center gap-1 py-1.5 text-xs text-red-700 hover:text-red-800 disabled:opacity-50"
                        data-testid="control-borrar"
                      >
                        {borrando === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Borrar
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <NuevoRegistroDialog
        open={nuevoOpen}
        onClose={() => setNuevoOpen(false)}
        tipo={tipo in TIPOS_CONTROL ? tipo : 'esterilizacion'}
        onGuardado={alGuardar}
      />

      {/* La foto en grande: entera y se puede acercar (pellizcando, con la
          rueda o con + / −), como las fotos del paciente. */}
      <Dialog open={!!visor} onOpenChange={(o) => { if (!o) setVisor(null); }}>
        <DialogContent className="max-w-4xl p-0 gap-0 bg-white border-none overflow-hidden" data-testid="control-visor">
          {visor && (
            <>
              <div className="px-4 py-3 pr-12 border-b border-border">
                <DialogTitle className="text-sm font-medium text-[#13231E]">
                  {conf.titulo} · {fechaCorta(visor.fecha)}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {hora(visor.creado_at)} · {visor.creado_por_nombre || 'Sin nombre'}
                  {visor.nota ? ` · ${visor.nota}` : ''}
                </DialogDescription>
              </div>
              <div className="relative bg-black flex items-center justify-center min-h-[240px]">
                <FotoZoom src={fotoUrl(visor)} alt={`${conf.titulo} del ${fechaCorta(visor.fecha)}`} testId="control-visor-foto" />
              </div>
              {visor.puede_borrar && (
                <div className="px-4 py-2 border-t border-border flex justify-end">
                  <button
                    type="button"
                    onClick={() => borrar(visor)}
                    disabled={borrando === visor.id}
                    className="inline-flex items-center gap-1 py-1.5 text-sm text-red-700 hover:text-red-800 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" /> Borrar registro
                  </button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
